import React from 'react';
import { NavLink } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../data/roles';

const {
  FiGrid, FiUsers, FiSpeaker, FiEyeOff, FiBook, FiBarChart2, FiShield, FiSettings, FiLock, FiChevronLeft, FiChevronRight, FiFileText
} = FiIcons;

const NAV_ITEMS = [
  { name: 'Dashboard', path: '/', icon: FiGrid, permission: PERMISSIONS.VIEW_DASHBOARD },
  { name: 'Learners', path: '/learners', icon: FiUsers, permission: PERMISSIONS.VIEW_LEARNERS },
  { name: 'Campaigns', path: '/campaigns', icon: FiSpeaker, permission: PERMISSIONS.VIEW_CAMPAIGNS },
  { name: 'Phishing', path: '/phishing', icon: FiEyeOff, permission: PERMISSIONS.VIEW_PHISHING },
  { name: 'Library', path: '/library', icon: FiBook, permission: PERMISSIONS.VIEW_LIBRARY },
  { name: 'Reports', path: '/reports', icon: FiBarChart2, permission: PERMISSIONS.VIEW_OWN_REPORTS },
  { name: 'Compliance', path: '/compliance', icon: FiShield, permission: PERMISSIONS.VIEW_COMPLIANCE },
  { name: 'Policies', path: '/policies', icon: FiFileText, permission: PERMISSIONS.MANAGE_SETTINGS },
  { name: 'Settings', path: '/settings', icon: FiSettings, permission: PERMISSIONS.VIEW_SETTINGS },
  { name: 'Roles', path: '/roles', icon: FiLock, permission: PERMISSIONS.MANAGE_SETTINGS },
];

const EMPLOYEE_ITEMS = [
  { name: 'Dashboard', path: '/employee/dashboard', icon: FiGrid, permission: PERMISSIONS.VIEW_OWN_TRAINING },
  { name: 'My History', path: '/employee/my-history', icon: FiBarChart2, permission: PERMISSIONS.VIEW_OWN_REPORTS },
  { name: 'Settings', path: '/employee/settings', icon: FiSettings, permission: PERMISSIONS.VIEW_OWN_TRAINING },
];

const MANAGER_ITEMS = [
  { name: 'My Team', path: '/my-team', icon: FiUsers, permission: PERMISSIONS.VIEW_OWN_TEAM },
  { name: 'Campaigns', path: '/campaigns', icon: FiSpeaker, permission: PERMISSIONS.VIEW_CAMPAIGNS },
  { name: 'Reports', path: '/reports', icon: FiBarChart2, permission: PERMISSIONS.VIEW_OWN_REPORTS },
  { name: 'Compliance', path: '/compliance', icon: FiShield, permission: PERMISSIONS.VIEW_COMPLIANCE },
];

const roleColors = {
  Administrator: { badge: 'bg-red-500/10 text-red-400 border-red-500/20' },
  'IT/Security Admin': { badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  Manager: { badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  Employee: { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { hasPermission, currentUser } = useAuth();

  // Safeguard against missing user context
  if (!currentUser) return null;

  const isEmployee = currentUser.role === 'Employee';
  const isManager = currentUser.role === 'Manager';

  let items = NAV_ITEMS;
  if (isEmployee) items = EMPLOYEE_ITEMS;
  else if (isManager) items = MANAGER_ITEMS;

  const visibleItems = items.filter(item => hasPermission(item.permission));
  const rc = roleColors[currentUser.role] || roleColors['IT/Security Admin'];

  return (
    <div className={`
      fixed bottom-0 left-0 right-0 w-full h-16 flex flex-row items-center z-50 bg-[#0A1128] text-slate-400
      md:top-0 md:bottom-auto md:h-screen ${collapsed ? 'md:w-16' : 'md:w-64'} md:flex-col md:shadow-xl transition-all duration-200
    `}>
      {/* Logo */}
      <div className={`hidden md:flex items-center ${collapsed ? 'justify-center px-0' : 'gap-3 px-5'} py-6`}>
        {/* Using the static logo from the public folder */}
        <img 
          src="/logo.svg" 
          alt="Company Logo" 
          className="w-8 h-8 rounded-lg flex-shrink-0 object-contain"
        />
        {!collapsed && (
          <div className="flex flex-col">
            <span className="text-white font-bold text-lg tracking-wide leading-tight">Sentinel</span>
            <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-[0.2em] leading-tight mt-0.5">Security Platform</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex flex-row md:flex-col flex-1 w-full md:w-auto px-2 md:px-3 py-0 md:py-2 space-x-1 md:space-x-0 md:space-y-1 overflow-x-auto md:overflow-x-hidden md:overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {visibleItems.map(item => (
          <NavLink key={item.name} to={item.path} end={item.path === '/'}
            className={({ isActive }) =>
              `flex flex-col md:flex-row items-center justify-center ${collapsed ? 'md:justify-center' : 'md:justify-start md:gap-3'}
               flex-shrink-0 w-16 md:w-auto px-0 md:px-3 py-2 md:py-2.5 rounded-lg transition-all text-sm font-medium group
              ${isActive
                ? 'text-[#00E5FF] md:bg-[#1A233A] md:text-white relative'
                : 'text-slate-400 hover:text-slate-200 md:hover:bg-white/5'}`
            }>
            {({ isActive }) => (
              <>
                {/* Mobile Active Indicator */}
                {isActive && <div className="absolute bottom-1 w-1 h-1 bg-[#00E5FF] rounded-full md:hidden" />}
                <SafeIcon icon={item.icon} className={`text-[22px] md:text-[18px] flex-shrink-0 transition-colors ${isActive ? 'text-[#00E5FF]' : ''} ${isActive ? 'mb-1 md:mb-0' : ''}`} />
                {!collapsed && <span className="hidden md:block">{item.name}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User + Collapse */}
      <div className="hidden md:block p-4 space-y-3 w-full">
        {!collapsed && (
          <div className="flex flex-col gap-2.5 bg-white/5 p-3 rounded-xl border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#1A233A] flex items-center justify-center text-[#00E5FF] font-bold text-sm flex-shrink-0 border border-white/10">
                {currentUser.avatar}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{currentUser.name}</p>
                <p className="text-xs text-slate-400 truncate">{currentUser.email}</p>
              </div>
            </div>
            <div className="flex items-center">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${rc.badge} border`}>
                {currentUser.role}
              </span>
            </div>
          </div>
        )}
        <button onClick={() => setCollapsed(c => !c)}
          className="w-full flex items-center justify-center py-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
          <SafeIcon icon={collapsed ? FiChevronRight : FiChevronLeft} className="text-sm" />
        </button>
      </div>
    </div>
  );
};

export default Sidebar;